import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Users, Star, ClipboardCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { STYLE_KEYS, STYLES, DEFAULT_STYLE, crmPercent } from "@/lib/disc";
import { StyleMeter } from "@/components/StyleMeter";
import SpecialistScoreDialog from "@/components/SpecialistScoreDialog";
import { SpecialistMatchLocked } from "@/components/SpecialistMatchLocked";
import { MatchDemandTracker } from "@/components/MatchDemandTracker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = {
  name: "",
  title: "",
  photo_url: "",
  bio: "",
  specialties: "",
  languages: "",
  style: { ...DEFAULT_STYLE },
  financing_expert: false,
  email: "",
  phone: "",
  years_experience: "",
  active: true,
};

export default function Specialists() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [scoring, setScoring] = useState(null);
  const [dealer, setDealer] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/specialists"), api.get("/dealer")])
      .then(([s, d]) => { setList(s.data); setDealer(d.data); })
      .catch(() => toast.error("Failed to load specialists"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);
  const unlocked = dealer?.addon_specialist_match === "active";

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      ...s,
      specialties: (s.specialties || []).join(", "),
      languages: (s.languages || []).join(", "),
      style: { ...DEFAULT_STYLE, ...(s.style || {}) },
      years_experience: s.years_experience ?? "",
    });
    setOpen(true);
  };

  const setStyle = (key, val) => setForm((f) => ({ ...f, style: { ...f.style, [key]: val } }));

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      specialties: form.specialties.split(",").map((x) => x.trim()).filter(Boolean),
      languages: form.languages.split(",").map((x) => x.trim()).filter(Boolean),
      years_experience: form.years_experience ? Number(form.years_experience) : null,
    };
    try {
      if (editing) await api.put(`/specialists/${editing.id}`, payload);
      else await api.post("/specialists", payload);
      toast.success(editing ? "Specialist updated" : "Specialist added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}?`)) return;
    try {
      await api.delete(`/specialists/${s.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Product Specialists</h1>
          <p className="text-sm text-slate-400 mt-1">
            Profiles the AI matcher uses to connect shoppers with the right associate. Style + CRM score both steer matching.
          </p>
        </div>
        {isAdmin && unlocked && (
          <Button
            onClick={openNew}
            data-testid="add-specialist-btn"
            className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shadow-lg shadow-blue-900/40"
          >
            <Plus className="mr-2 h-4 w-4" /> Add specialist
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" />
        </div>
      ) : !unlocked ? (
        <div className="space-y-6">
          <MatchDemandTracker />
          <SpecialistMatchLocked dealer={dealer} onRequested={setDealer} />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((s) => (
            <div
              key={s.id}
              data-testid={`specialist-card-${s.id}`}
              className="tracing-top rounded-xl border border-[#2A2E3D] bg-[#12141C] p-5"
            >
              <div className="flex items-start gap-4">
                <img
                  src={s.photo_url}
                  alt={s.name}
                  className="h-16 w-16 rounded-full object-cover border-2 border-[#0A8CF2]"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold truncate">{s.name}</div>
                    {!s.active && <Badge variant="outline" className="border-slate-600 text-slate-400">off</Badge>}
                  </div>
                  <div className="text-xs text-slate-400">{s.title}</div>
                  {s.years_experience != null && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{s.years_experience} yrs experience</div>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-400 mt-3 line-clamp-3">{s.bio}</p>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2">
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1">Style</div>
                  <StyleMeter style={s.style} testId={`specialist-style-${s.id}`} />
                </div>
                <button
                  type="button"
                  onClick={() => setScoring(s)}
                  data-testid={`score-specialist-${s.id}`}
                  className="text-left rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 hover:border-[#0A8CF2]/60 transition-colors"
                >
                  <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold mb-1 flex items-center gap-1">
                    <ClipboardCheck className="h-3 w-3" /> CRM score
                  </div>
                  <div className="font-heading text-2xl font-black leading-none" data-testid={`specialist-crm-score-${s.id}`}>
                    {crmPercent(s) == null ? <span className="text-slate-600 text-base">Not rated</span> : `${crmPercent(s)}%`}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {crmPercent(s) == null ? "Tap to score" : `Follow-up ${s.follow_up_score ?? "—"}/5 · Tasks ${s.task_score ?? "—"}/5`}
                  </div>
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(s.specialties || []).slice(0, 5).map((sp) => (
                  <span key={sp} className="text-[10px] px-2 py-1 rounded-full bg-slate-900 border border-slate-800 text-slate-400">
                    {sp}
                  </span>
                ))}
                {s.financing_expert && (
                  <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-950/50 border border-emerald-800/50 text-emerald-400 flex items-center gap-1">
                    <Star className="h-2.5 w-2.5" /> Financing
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    onClick={() => openEdit(s)}
                    data-testid={`edit-specialist-${s.id}`}
                    className="flex-1 h-9 border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200"
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => remove(s)}
                    data-testid={`delete-specialist-${s.id}`}
                    className="h-9 border-red-900/50 bg-red-950/30 hover:bg-red-950/60 text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold flex items-center gap-2">
              <Users className="h-5 w-5 text-[#0A8CF2]" />
              {editing ? "Edit specialist" : "New specialist"}
            </DialogTitle>
            <DialogDescription className="sr-only">Specialist profile, communication style and contact details used by the matcher.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[
              ["name", "Name", "text"],
              ["title", "Title", "text"],
              ["photo_url", "Photo URL", "text"],
              ["email", "Email", "email"],
              ["phone", "Phone", "text"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className="text-slate-300 mb-1 block">{label}</Label>
                <Input
                  type={type}
                  data-testid={`specialist-${key}-input`}
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
                />
              </div>
            ))}
            <div>
              <Label className="text-slate-300 mb-1 block">Bio</Label>
              <Textarea
                value={form.bio}
                data-testid="specialist-bio-input"
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-1 block">Specialties (comma separated)</Label>
              <Input
                value={form.specialties}
                data-testid="specialist-specialties-input"
                onChange={(e) => setForm({ ...form, specialties: e.target.value })}
                placeholder="SUV, EV, Financing, Rogue"
                className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-1 block">Communication style (DISC-style profile)</Label>
              <p className="text-xs text-slate-500 mb-3">Shoppers take a 4-question style check — rate how strongly each style describes this specialist.</p>
              <div className="space-y-3" data-testid="specialist-style-picker">
                {STYLE_KEYS.map((k) => (
                  <div key={k} className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2.5">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono-tech font-black text-xs w-5 h-5 rounded flex items-center justify-center text-white" style={{ background: STYLES[k].color }}>{k}</span>
                        <span className="font-medium">{STYLES[k].name}</span>
                        <span className="text-[11px] text-slate-500">{STYLES[k].specialist}</span>
                      </div>
                      <span className="font-mono-tech text-xs text-slate-400" data-testid={`style-value-${k}`}>{form.style[k]}/10</span>
                    </div>
                    <Slider
                      data-testid={`style-slider-${k}`}
                      value={[form.style[k]]}
                      min={0}
                      max={10}
                      step={1}
                      onValueChange={([v]) => setStyle(k, v)}
                      className="[&_[role=slider]]:border-[#0A8CF2] [&_[role=slider]]:bg-[#0F111A] [&>span:first-child]:bg-slate-800 [&>span:first-child>span]:bg-[#0A8CF2]"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300 mb-1 block">Languages</Label>
                <Input
                  value={form.languages}
                  onChange={(e) => setForm({ ...form, languages: e.target.value })}
                  placeholder="English, Spanish"
                  className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300 mb-1 block">Years exp.</Label>
                <Input
                  type="number"
                  value={form.years_experience}
                  onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                  className="bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100"
                />
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div className="flex items-center gap-2 text-sm">
                <Star className="h-4 w-4 text-emerald-400" /> Financing expert
              </div>
              <Switch
                checked={form.financing_expert}
                onCheckedChange={(v) => setForm({ ...form, financing_expert: v })}
                data-testid="specialist-financing-switch"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-4 py-3">
              <div className="text-sm">Active (visible to matcher)</div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
                data-testid="specialist-active-switch"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={save}
              disabled={saving || !form.name}
              data-testid="save-specialist-btn"
              className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SpecialistScoreDialog
        specialist={scoring}
        open={!!scoring}
        onOpenChange={(o) => !o && setScoring(null)}
        onSaved={(updated) => setList((l) => l.map((x) => (x.id === updated.id ? updated : x)))}
      />
    </div>
  );
}
